import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import {
  calcDepreciation, depreciationSchedule,
  defaultDepreciationForCategory, bookValueColor,
  conditionFromBookValue, fmtKES,
} from '../../lib/depreciation';

// ── Default categories ────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  { name:'Church Furniture',              icon:'🪑', description:'Pews, chairs, pulpit, altar tables' },
  { name:'Audio & Visual Systems',        icon:'🔊', description:'Microphones, speakers, projectors, screens, cameras' },
  { name:'IT & Administrative Systems',   icon:'💻', description:'Computers, printers, routers, phones, UPS' },
  { name:'Facilities & Building Systems', icon:'🏗️', description:'CCTV, alarms, fire extinguishers, generators' },
  { name:'Kitchen Appliances & Utensils', icon:'🍳', description:'Cookers, fridges, pots, plates, cutlery' },
  { name:'Hall Seats & Tables',           icon:'🪑', description:'Plastic chairs, foldable tables, benches' },
  { name:'Church School Items',           icon:'🎓', description:'Costumes, seats, teaching materials, toys', subcategories:['Costumes','Seats','Teaching Materials','Toys & Games','Whiteboards'] },
  { name:'Office Items',                  icon:'📁', description:'Stationery, files, shelves, notice boards' },
  { name:'Boys & Girls Brigade',          icon:'🎖️', description:'Uniforms and equipment', subcategories:['Shirts','Trousers','Dresses / Skirts','Caps / Berets','Belts','Flags','Drums & Instruments','Other Equipment'] },
  { name:"Women's Guild Items",           icon:'👩', description:'Tents, cups, sufuria, trays, tablecloths', subcategories:['Tents','Cups & Mugs','Sufuria / Pots','Serving Trays','Tablecloths','Uniforms','Other'] },
  { name:'Musical Instruments',           icon:'🎹', description:'Piano, keyboard, guitar, drums, amplifiers' },
  { name:'Vehicles & Transport',          icon:'🚐', description:'Church vehicles and motorcycles' },
];

const CONDITION_COLORS = {
  Excellent:      { bg:'#E8F5E9', color:'#1A7A32' },
  Good:           { bg:'#E8F5E9', color:'#2E7D32' },
  Fair:           { bg:'#FFF8E0', color:'#C47A00' },
  Poor:           { bg:'#FFEBEE', color:'#CC0000' },
  'Needs Repair': { bg:'#FFEBEE', color:'#CC0000' },
  Disposed:       { bg:'#ECEFF1', color:'#546E7A' },
};
const UNITS = ['pcs','pairs','sets','boxes','bags','litres','metres','kg','rolls'];
const DEP_METHODS = [
  { value:'straight_line',    label:'Straight Line (SLM)' },
  { value:'reducing_balance', label:'Reducing Balance (RBM)' },
  { value:'none',             label:'No Depreciation' },
];

const blankItem = {
  name:'', description:'', quantity:1, unit:'pcs', condition:'Good',
  location:'', serial_number:'', purchase_date:'', purchase_price:'',
  subcategory:'', notes:'',
  depreciation_method:'straight_line', useful_life_years:5,
  salvage_value:'', depreciation_rate:20,
};
const blankCat = { name:'', icon:'📦', description:'' };

export default function InventoryPage() {
  const [categories,    setCategories]    = useState([]);
  const [items,         setItems]         = useState([]);
  const [selCat,        setSelCat]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [showAddItem,   setShowAddItem]   = useState(false);
  const [showAddCat,    setShowAddCat]    = useState(false);
  const [showSchedule,  setShowSchedule]  = useState(null); // item for schedule modal
  const [editItem,      setEditItem]      = useState(null);
  const [search,        setSearch]        = useState('');
  const [condFilter,    setCondFilter]    = useState('');
  const [saving,        setSaving]        = useState(false);
  const [itemForm,      setItemForm]      = useState({ ...blankItem });
  const [catForm,       setCatForm]       = useState({ ...blankCat });

  useEffect(() => { init(); }, []);
  useEffect(() => { if (selCat) fetchItems(selCat.id); else setItems([]); }, [selCat]);

  async function init() {
    setLoading(true);
    let { data: existing } = await supabase.from('inventory_categories').select('*').order('name');
    if (!existing || existing.length === 0) {
      const rows = DEFAULT_CATEGORIES.map(c => ({ name:c.name, icon:c.icon, description:c.description, subcategories:c.subcategories||[] }));
      const { data: inserted } = await supabase.from('inventory_categories').insert(rows).select();
      existing = inserted || [];
    }
    setCategories(existing);
    if (existing.length > 0) setSelCat(existing[0]);
    setLoading(false);
  }

  async function fetchItems(catId) {
    const { data } = await supabase.from('inventory_items').select('*').eq('category_id', catId).order('name');
    setItems(data || []);
  }

  const setI = (k) => (e) => setItemForm(f => ({ ...f, [k]: e.target.value }));

  // When category changes, pre-fill depreciation defaults
  function applyDepreciationDefaults(categoryName) {
    const d = defaultDepreciationForCategory(categoryName);
    setItemForm(f => ({
      ...f,
      depreciation_method: d.method,
      depreciation_rate:   d.rate,
      useful_life_years:   d.life,
      salvage_value:       d.salvage,
    }));
  }

  async function handleSaveItem(e) {
    e.preventDefault(); setSaving(true);
    const payload = {
      ...itemForm,
      category_id:          selCat.id,
      quantity:             Number(itemForm.quantity) || 0,
      purchase_price:       itemForm.purchase_price  ? Number(itemForm.purchase_price)  : null,
      salvage_value:        itemForm.salvage_value   ? Number(itemForm.salvage_value)   : 0,
      depreciation_rate:    Number(itemForm.depreciation_rate)   || 20,
      useful_life_years:    Number(itemForm.useful_life_years)   || 5,
      purchase_date:        itemForm.purchase_date   || null,
    };
    if (editItem) {
      const { error } = await supabase.from('inventory_items').update(payload).eq('id', editItem.id);
      if (error) toast.error(error.message);
      else { toast.success('Item updated ✅'); setEditItem(null); setShowAddItem(false); }
    } else {
      const { error } = await supabase.from('inventory_items').insert(payload);
      if (error) toast.error(error.message);
      else { toast.success('Item added ✅'); setShowAddItem(false); }
    }
    setItemForm({ ...blankItem });
    fetchItems(selCat.id);
    setSaving(false);
  }

  async function handleDeleteItem(id) {
    if (!window.confirm('Remove this item from inventory?')) return;
    await supabase.from('inventory_items').delete().eq('id', id);
    toast.success('Item removed');
    fetchItems(selCat.id);
  }

  async function handleSaveCat(e) {
    e.preventDefault(); setSaving(true);
    const { data, error } = await supabase.from('inventory_categories')
      .insert({ name:catForm.name, icon:catForm.icon, description:catForm.description, subcategories:[] })
      .select().single();
    if (error) toast.error(error.message);
    else { toast.success('Category added ✅'); setCategories(c=>[...c,data]); setSelCat(data); setShowAddCat(false); setCatForm({...blankCat}); }
    setSaving(false);
  }

  async function handleDeleteCat(cat) {
    if (!window.confirm(`Delete "${cat.name}" and ALL its items?`)) return;
    await supabase.from('inventory_items').delete().eq('category_id', cat.id);
    await supabase.from('inventory_categories').delete().eq('id', cat.id);
    toast.success('Category removed');
    const rest = categories.filter(c => c.id !== cat.id);
    setCategories(rest); setSelCat(rest[0]||null); setItems([]);
  }

  function openEditItem(item) {
    setItemForm({
      name:                item.name,
      description:         item.description||'',
      quantity:            item.quantity,
      unit:                item.unit||'pcs',
      condition:           item.condition||'Good',
      location:            item.location||'',
      serial_number:       item.serial_number||'',
      purchase_date:       item.purchase_date||'',
      purchase_price:      item.purchase_price||'',
      subcategory:         item.subcategory||'',
      notes:               item.notes||'',
      depreciation_method: item.depreciation_method||'straight_line',
      useful_life_years:   item.useful_life_years||5,
      salvage_value:       item.salvage_value||'',
      depreciation_rate:   item.depreciation_rate||20,
    });
    setEditItem(item); setShowAddItem(true);
  }

  // ── Derived data ──────────────────────────────────────────────
  const filteredItems = items.filter(i => {
    const matchSearch = !search || `${i.name} ${i.description} ${i.subcategory}`.toLowerCase().includes(search.toLowerCase());
    const matchCond   = !condFilter || i.condition === condFilter;
    return matchSearch && matchCond;
  });

  // Category totals WITH depreciation
  const totalOriginalValue = items.reduce((s,i) => s + (Number(i.purchase_price)||0)*Number(i.quantity), 0);
  const totalBookValue     = items.reduce((s,i) => s + calcDepreciation(i).totalBookValue, 0);
  const totalDepreciation  = totalOriginalValue - totalBookValue;
  const subcats = selCat?.subcategories || [];

  if (loading) return <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading inventory…</div>;

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{fontSize:20,fontWeight:800}}>📦 Church Asset Register</h1>
          <div style={{fontSize:13,color:'var(--text3)',marginTop:2}}>Track assets, current book values and depreciation</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddCat(true)}>+ Add Category</button>
      </div>

      <div style={{display:'flex',gap:18,alignItems:'flex-start'}}>

        {/* ── Category sidebar ── */}
        <div style={{width:220,flexShrink:0}}>
          <div className="card" style={{overflow:'hidden'}}>
            <div style={{padding:'11px 14px',background:'var(--blue-dark)',color:'white',fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:'uppercase'}}>
              Categories ({categories.length})
            </div>
            <div style={{maxHeight:'72vh',overflowY:'auto'}}>
              {categories.map(cat => {
                const active = selCat?.id === cat.id;
                return (
                  <div key={cat.id} onClick={() => setSelCat(cat)} style={{padding:'10px 13px',cursor:'pointer',display:'flex',alignItems:'center',gap:9,background:active?'var(--blue-light)':'white',borderBottom:'1px solid var(--border)',borderLeft:`3px solid ${active?'var(--blue)':'transparent'}`,transition:'all .12s'}}>
                    <span style={{fontSize:17,flexShrink:0}}>{cat.icon}</span>
                    <div style={{fontSize:12,fontWeight:active?700:400,color:active?'var(--blue-dark)':'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cat.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Main panel ── */}
        <div style={{flex:1,minWidth:0}}>
          {selCat && (
            <>
              {/* Category banner */}
              <div style={{background:'linear-gradient(135deg,var(--blue-dark),var(--blue))',borderRadius:12,padding:'16px 20px',color:'white',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                <div>
                  <div style={{fontSize:22,marginBottom:2}}>{selCat.icon}</div>
                  <div style={{fontWeight:800,fontSize:17}}>{selCat.name}</div>
                  <div style={{opacity:.75,fontSize:12}}>{selCat.description}</div>
                </div>
                <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                  {[
                    ['Items',    items.length,                                           '📋'],
                    ['Orig Val', totalOriginalValue>0?`KES ${Math.round(totalOriginalValue/1000)}k`:'—', '💰'],
                    ['Book Val', totalBookValue>0    ?`KES ${Math.round(totalBookValue/1000)}k`    :'—', '📉'],
                    ['Dep',      totalOriginalValue>0?`${Math.round((totalDepreciation/totalOriginalValue)*100)}%`:'0%','⬇️'],
                  ].map(([l,v,ic])=>(
                    <div key={l} style={{textAlign:'center'}}>
                      <div style={{fontSize:9,opacity:.65,textTransform:'uppercase',letterSpacing:.5}}>{l}</div>
                      <div style={{fontSize:15,fontWeight:800}}>{ic} {v}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-sm" style={{background:'rgba(255,255,255,.15)',color:'white',border:'1px solid rgba(255,255,255,.3)'}}
                    onClick={() => { setItemForm({...blankItem}); setEditItem(null); applyDepreciationDefaults(selCat.name); setShowAddItem(true); }}>+ Add Item</button>
                  <button className="btn btn-sm" style={{background:'rgba(204,0,0,.3)',color:'white',border:'1px solid rgba(204,0,0,.5)'}}
                    onClick={() => handleDeleteCat(selCat)}>🗑 Delete</button>
                </div>
              </div>

              {/* Search + filter */}
              <div className="card mb-3">
                <div className="card-pad" style={{paddingTop:10,paddingBottom:10}}>
                  <div className="flex gap-2 flex-wrap">
                    <div className="search-bar" style={{flex:'1 1 180px'}}>
                      🔍 <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search ${selCat.name}…`}/>
                      {search&&<button onClick={()=>setSearch('')} style={{background:'none',border:'none',color:'var(--text3)',cursor:'pointer',fontSize:16}}>✕</button>}
                    </div>
                    <select className="form-select" style={{width:'auto'}} value={condFilter} onChange={e=>setCondFilter(e.target.value)}>
                      <option value="">All Conditions</option>
                      {Object.keys(CONDITION_COLORS).map(c=><option key={c}>{c}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={() => { setItemForm({...blankItem}); setEditItem(null); applyDepreciationDefaults(selCat.name); setShowAddItem(true); }}>+ Add Item</button>
                  </div>
                </div>
              </div>

              {/* Items table */}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      {subcats.length>0&&<th>Sub-type</th>}
                      <th>Qty</th>
                      <th>Condition</th>
                      <th>Purchase Price</th>
                      <th>Dep. Method</th>
                      <th>Current Book Value</th>
                      <th>Dep %</th>
                      <th>Location</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length===0&&(
                      <tr><td colSpan={subcats.length>0?10:9} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>
                        No items yet. Click <strong>+ Add Item</strong> to get started.
                      </td></tr>
                    )}
                    {filteredItems.map(item => {
                      const dep   = calcDepreciation(item);
                      const cond  = CONDITION_COLORS[item.condition]||{bg:'#F5F5F5',color:'#546E7A'};
                      const bvClr = bookValueColor(dep.pctDepreciated);
                      return (
                        <tr key={item.id}>
                          <td>
                            <div style={{fontWeight:700}}>{item.name}</div>
                            {item.description&&<div style={{fontSize:11,color:'var(--text3)'}}>{item.description}</div>}
                            {item.serial_number&&<div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>S/N: {item.serial_number}</div>}
                          </td>
                          {subcats.length>0&&<td style={{fontSize:12}}>{item.subcategory||'—'}</td>}
                          <td style={{fontWeight:800,fontSize:16,color:'var(--blue-dark)',textAlign:'center'}}>{item.quantity}</td>
                          <td><span style={{fontSize:11,fontWeight:700,padding:'3px 9px',borderRadius:100,background:cond.bg,color:cond.color}}>{item.condition}</span></td>
                          <td style={{fontSize:12}}>
                            {item.purchase_price ? fmtKES(item.purchase_price) : '—'}
                            {item.purchase_date&&<div style={{fontSize:10,color:'var(--text3)'}}>{new Date(item.purchase_date).toLocaleDateString('en-KE',{year:'numeric',month:'short'})}</div>}
                          </td>
                          <td style={{fontSize:11}}>
                            {item.depreciation_method==='straight_line'
                              ? <span className="badge badge-blue" style={{fontSize:10}}>SLM · {item.useful_life_years}yr</span>
                              : item.depreciation_method==='reducing_balance'
                              ? <span className="badge badge-amber" style={{fontSize:10,background:'#FFF8E0',color:'#C47A00'}}>RBM · {item.depreciation_rate}%</span>
                              : <span className="badge badge-gray" style={{fontSize:10}}>None</span>}
                          </td>
                          {/* Current Book Value — the key column */}
                          <td>
                            {item.purchase_price ? (
                              <div>
                                <div style={{fontWeight:800,fontSize:14,color:bvClr.color,background:bvClr.bg,padding:'3px 8px',borderRadius:6,display:'inline-block'}}>
                                  {fmtKES(dep.bookValue)}
                                </div>
                                {item.quantity>1&&<div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>Total: {fmtKES(dep.totalBookValue)}</div>}
                              </div>
                            ) : <span style={{color:'var(--text3)',fontSize:12}}>—</span>}
                          </td>
                          {/* Depreciation % */}
                          <td>
                            {item.purchase_price&&dep.pctDepreciated>0 ? (
                              <div style={{minWidth:70}}>
                                <div style={{height:6,background:'var(--bg)',borderRadius:3,overflow:'hidden',marginBottom:3}}>
                                  <div style={{height:'100%',width:`${dep.pctDepreciated}%`,background:dep.pctDepreciated>70?'var(--red)':dep.pctDepreciated>40?'#C47A00':'var(--success)',borderRadius:3,transition:'width .4s'}}/>
                                </div>
                                <span style={{fontSize:11,fontWeight:700,color:bvClr.color}}>{dep.pctDepreciated}%</span>
                                {dep.isFullyDep&&<div style={{fontSize:10,color:'var(--text3)'}}>Fully depreciated</div>}
                              </div>
                            ) : <span style={{color:'var(--text3)',fontSize:12}}>—</span>}
                          </td>
                          <td style={{fontSize:12}}>{item.location||'—'}</td>
                          <td>
                            <div className="flex gap-1" style={{flexWrap:'nowrap'}}>
                              {item.purchase_price&&item.purchase_date&&(
                                <button className="btn btn-sm" style={{background:'var(--blue-light)',color:'var(--blue-dark)',border:'none',fontSize:11}} onClick={()=>setShowSchedule(item)} title="View depreciation schedule">📉</button>
                              )}
                              <button className="btn btn-sm btn-blue" onClick={()=>openEditItem(item)}>Edit</button>
                              <button className="btn btn-sm btn-danger" onClick={()=>handleDeleteItem(item.id)}>🗑</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    {filteredItems.length>0&&totalOriginalValue>0&&(
                      <tr style={{background:'var(--blue-dark)',color:'white',fontWeight:700}}>
                        <td colSpan={subcats.length>0?4:3}>TOTALS ({filteredItems.length} items)</td>
                        <td style={{fontSize:12}}>{fmtKES(totalOriginalValue)}</td>
                        <td></td>
                        <td>
                          <div style={{fontWeight:900,fontSize:14}}>{fmtKES(totalBookValue)}</div>
                          <div style={{fontSize:10,opacity:.7}}>Current value</div>
                        </td>
                        <td>
                          <div>{Math.round((totalDepreciation/Math.max(totalOriginalValue,1))*100)}% dep</div>
                          <div style={{fontSize:10,opacity:.7}}>{fmtKES(totalDepreciation)} lost</div>
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!selCat&&<div className="alert alert-info">Select a category from the left to view items.</div>}
        </div>
      </div>

      {/* ── DEPRECIATION SCHEDULE MODAL ── */}
      {showSchedule&&(
        <Modal title={`📉 Depreciation Schedule — ${showSchedule.name}`} onClose={()=>setShowSchedule(null)} large
          footer={<button className="btn btn-ghost" onClick={()=>setShowSchedule(null)}>Close</button>}>
          <DepreciationScheduleView item={showSchedule}/>
        </Modal>
      )}

      {/* ── ADD / EDIT ITEM MODAL ── */}
      {showAddItem&&(
        <Modal
          title={editItem?`Edit — ${editItem.name}`:`Add Item to ${selCat?.name}`}
          onClose={()=>{setShowAddItem(false);setEditItem(null);setItemForm({...blankItem});}}
          large
          footer={<>
            <button className="btn btn-ghost" onClick={()=>{setShowAddItem(false);setEditItem(null);setItemForm({...blankItem});}}>Cancel</button>
            <button className="btn btn-primary" form="item-form" type="submit" disabled={saving}>{saving?'Saving…':editItem?'Save Changes':'Add Item'}</button>
          </>}
        >
          <form id="item-form" onSubmit={handleSaveItem}>
            {/* Basic info */}
            <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>Item Details</div>
            <div className="grid-2">
              <div className="form-group" style={{gridColumn:'1/-1'}}>
                <label className="form-label">Item Name *</label>
                <input className="form-input" value={itemForm.name} onChange={setI('name')} placeholder="e.g. Plastic Chair, Microphone, Projector" required/>
              </div>
              {subcats.length>0&&(
                <div className="form-group">
                  <label className="form-label">Sub-type</label>
                  <select className="form-select" value={itemForm.subcategory} onChange={setI('subcategory')}>
                    <option value="">Select…</option>
                    {subcats.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label className="form-label">Quantity *</label><input className="form-input" type="number" min="0" value={itemForm.quantity} onChange={setI('quantity')} required/></div>
              <div className="form-group"><label className="form-label">Unit</label><select className="form-select" value={itemForm.unit} onChange={setI('unit')}>{UNITS.map(u=><option key={u}>{u}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Condition</label><select className="form-select" value={itemForm.condition} onChange={setI('condition')}>{Object.keys(CONDITION_COLORS).map(c=><option key={c}>{c}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={itemForm.location} onChange={setI('location')} placeholder="e.g. Main Store, Sacristy"/></div>
              <div className="form-group"><label className="form-label">Serial / Code No.</label><input className="form-input" value={itemForm.serial_number} onChange={setI('serial_number')} placeholder="Optional identifier"/></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={itemForm.notes} onChange={setI('notes')}/></div>
            </div>

            {/* Purchase & Depreciation */}
            <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5,margin:'16px 0 10px',paddingTop:16,borderTop:'1px solid var(--border)'}}>Purchase & Depreciation</div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Purchase Date</label>
                <input className="form-input" type="date" value={itemForm.purchase_date} onChange={setI('purchase_date')}/>
              </div>
              <div className="form-group">
                <label className="form-label">Purchase Price (KES) — per unit</label>
                <input className="form-input" type="number" min="0" value={itemForm.purchase_price} onChange={setI('purchase_price')} placeholder="0"/>
              </div>
              <div className="form-group">
                <label className="form-label">Depreciation Method</label>
                <select className="form-select" value={itemForm.depreciation_method} onChange={setI('depreciation_method')}>
                  {DEP_METHODS.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              {itemForm.depreciation_method==='straight_line'&&<>
                <div className="form-group">
                  <label className="form-label">Useful Life (years)</label>
                  <input className="form-input" type="number" min="1" max="100" value={itemForm.useful_life_years} onChange={setI('useful_life_years')}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Salvage / Residual Value (KES)</label>
                  <input className="form-input" type="number" min="0" value={itemForm.salvage_value} onChange={setI('salvage_value')} placeholder="0"/>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>The estimated value at end of useful life. Use 0 if fully written off.</div>
                </div>
              </>}

              {itemForm.depreciation_method==='reducing_balance'&&(
                <div className="form-group">
                  <label className="form-label">Annual Depreciation Rate (%)</label>
                  <input className="form-input" type="number" min="1" max="100" value={itemForm.depreciation_rate} onChange={setI('depreciation_rate')} placeholder="20"/>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>e.g. 25 = 25% per year. Vehicles: 25%, Electronics: 33%, Computers: 33%</div>
                </div>
              )}
            </div>

            {/* Live preview */}
            {itemForm.purchase_price&&itemForm.purchase_date&&itemForm.depreciation_method!=='none'&&(
              <LiveDepreciationPreview item={itemForm}/>
            )}
          </form>
        </Modal>
      )}

      {/* ── ADD CATEGORY MODAL ── */}
      {showAddCat&&(
        <Modal title="Add New Category" onClose={()=>{setShowAddCat(false);setCatForm({...blankCat});}}
          footer={<><button className="btn btn-ghost" onClick={()=>setShowAddCat(false)}>Cancel</button><button className="btn btn-primary" form="cat-form" type="submit" disabled={saving}>{saving?'Saving…':'Add Category'}</button></>}>
          <form id="cat-form" onSubmit={handleSaveCat}>
            <div className="form-group"><label className="form-label">Category Name *</label><input className="form-input" value={catForm.name} onChange={e=>setCatForm(f=>({...f,name:e.target.value}))} required/></div>
            <div className="form-group"><label className="form-label">Icon (emoji)</label><input className="form-input" value={catForm.icon} onChange={e=>setCatForm(f=>({...f,icon:e.target.value}))} style={{fontSize:20}} placeholder="📦"/></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" rows={2} value={catForm.description} onChange={e=>setCatForm(f=>({...f,description:e.target.value}))}/></div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ── Live preview in the add/edit form ─────────────────────────
function LiveDepreciationPreview({ item }) {
  const dep = calcDepreciation(item);
  const bvc = bookValueColor(dep.pctDepreciated);
  return (
    <div style={{background:'var(--blue-light)',border:'1.5px solid var(--blue)',borderRadius:10,padding:'14px 16px',marginTop:8}}>
      <div style={{fontSize:11,fontWeight:700,color:'var(--blue-dark)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>📉 Live Depreciation Preview</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10}}>
        {[
          ['Method',           dep.method],
          ['Years in Use',     dep.yearsUsed.toFixed(1) + ' yrs'],
          ['Original Cost',    fmtKES(dep.cost)],
          ['Total Depreciated',fmtKES(dep.totalDep)],
          ['Current Book Value',fmtKES(dep.bookValue)],
          dep.salvageValue>0?['Salvage Value',fmtKES(dep.salvageValue)]:null,
          dep.method==='Straight Line'?['Annual Dep',fmtKES(dep.annualDep)]:null,
          dep.method==='Reducing Balance'?['This Year Dep',fmtKES(dep.thisYearDep)]:null,
          dep.yearsRemaining!=null?['Years Remaining',dep.yearsRemaining.toFixed(1)]:null,
        ].filter(Boolean).map(([l,v])=>(
          <div key={l} style={{background:'white',borderRadius:7,padding:'8px 12px'}}>
            <div style={{fontSize:10,color:'var(--text3)',fontWeight:700,textTransform:'uppercase',marginBottom:3}}>{l}</div>
            <div style={{fontSize:13,fontWeight:700,color:l.includes('Book')?bvc.color:'var(--text)'}}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{marginTop:10}}>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--blue-dark)',marginBottom:3}}>
          <span>Depreciated</span><span>{dep.pctDepreciated}%</span>
        </div>
        <div style={{height:8,background:'rgba(0,51,153,.1)',borderRadius:4,overflow:'hidden'}}>
          <div style={{height:'100%',width:`${dep.pctDepreciated}%`,background:dep.pctDepreciated>70?'var(--red)':dep.pctDepreciated>40?'#C47A00':'var(--blue)',borderRadius:4,transition:'width .3s'}}/>
        </div>
      </div>
    </div>
  );
}

// ── Depreciation schedule table ───────────────────────────────
function DepreciationScheduleView({ item }) {
  const dep      = calcDepreciation(item);
  const schedule = depreciationSchedule(item);
  const bvc      = bookValueColor(dep.pctDepreciated);
  const currentYear = new Date().getFullYear();

  return (
    <div>
      {/* Summary cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:12,marginBottom:20}}>
        {[
          ['Original Cost',      fmtKES(Number(item.purchase_price)||0), 'var(--text)'],
          ['Current Book Value', fmtKES(dep.bookValue),                   bvc.color],
          ['Total Depreciation', fmtKES(dep.totalDep),                    'var(--red)'],
          ['% Depreciated',      `${dep.pctDepreciated}%`,                bvc.color],
          ['Method',             dep.method,                              'var(--blue-dark)'],
          ['Years in Use',       `${dep.yearsUsed.toFixed(1)} years`,      'var(--text)'],
        ].map(([l,v,c])=>(
          <div key={l} style={{background:'var(--bg)',borderRadius:8,padding:'10px 14px'}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>{l}</div>
            <div style={{fontSize:15,fontWeight:800,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Schedule table */}
      <div style={{overflowX:'auto'}}>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Annual Depreciation</th>
              <th>Accumulated Depreciation</th>
              <th>Book Value (End of Year)</th>
              <th>% Remaining</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((row,i) => {
              const isCurrentYear = row.year === currentYear;
              const isFuture      = row.year > currentYear;
              return (
                <tr key={row.year} style={{
                  background: isCurrentYear ? 'rgba(0,51,153,0.07)' : isFuture ? 'var(--bg)' : 'white',
                  fontWeight: isCurrentYear ? 700 : 400,
                  opacity:    isFuture ? .6 : 1,
                }}>
                  <td>
                    {row.year}
                    {row.yearNo===0&&<span style={{fontSize:10,marginLeft:6,padding:'1px 6px',background:'var(--blue-light)',color:'var(--blue-dark)',borderRadius:100,fontWeight:700}}>Purchase</span>}
                    {isCurrentYear&&<span style={{fontSize:10,marginLeft:6,padding:'1px 6px',background:'var(--blue)',color:'white',borderRadius:100,fontWeight:700}}>This Year</span>}
                  </td>
                  <td style={{color:'var(--red)',fontWeight:600}}>{row.yearNo===0?'—':fmtKES(row.dep)}</td>
                  <td style={{color:'var(--text2)'}}>{fmtKES(Number(item.purchase_price||0)-row.bookValue)}</td>
                  <td>
                    <span style={{fontWeight:700,fontSize:14,color:bookValueColor(row.pct).color,background:bookValueColor(row.pct).bg,padding:'2px 10px',borderRadius:6}}>
                      {fmtKES(row.bookValue)}
                    </span>
                  </td>
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:60,height:6,background:'var(--bg)',borderRadius:3,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${100-row.pct}%`,background:bookValueColor(row.pct).color,borderRadius:3}}/>
                      </div>
                      <span style={{fontSize:11,fontWeight:700,color:bookValueColor(row.pct).color}}>{100-row.pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="alert alert-info mt-4" style={{fontSize:12}}>
        <strong>Method:</strong> {dep.method}
        {item.depreciation_method==='straight_line'
          ? ` · Useful life: ${item.useful_life_years} years · Salvage value: ${fmtKES(item.salvage_value||0)}`
          : ` · Annual rate: ${item.depreciation_rate}%`}
        · <strong>Current condition:</strong> {item.condition}
      </div>
    </div>
  );
}